import { useEffect, useState } from "react";
import { X, Smartphone, Share } from "lucide-react";

/**
 * Add-to-Home-Screen prompt.
 *
 * - Android/Chrome/Edge: listens for `beforeinstallprompt` and shows our own
 *   branded prompt that triggers the native install dialog on tap.
 * - iOS Safari (no beforeinstallprompt support): detects iOS-not-standalone
 *   and shows step-by-step Share → Add to Home Screen instructions.
 * - Once dismissed (or installed) we set localStorage so we don't nag.
 *
 * Mounted globally in App.js. Renders nothing if already installed/declined.
 */
const DISMISSED_KEY = "smart_pwa_install_dismissed_at";
const DISMISS_COOLDOWN_DAYS = 7;

function isStandalone() {
    return (
        window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator.standalone === true
    );
}

function isIOS() {
    return /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function recentlyDismissed() {
    const at = localStorage.getItem(DISMISSED_KEY);
    if (!at) return false;
    const days = (Date.now() - Number(at)) / (1000 * 60 * 60 * 24);
    return days < DISMISS_COOLDOWN_DAYS;
}

export default function PWAInstallPrompt() {
    const [deferred, setDeferred] = useState(null);
    const [show, setShow] = useState(false);
    const [iosFallback, setIosFallback] = useState(false);

    useEffect(() => {
        if (isStandalone() || recentlyDismissed()) return;

        const onPrompt = (e) => {
            e.preventDefault();
            setDeferred(e);
            // Slight delay so it doesn't feel intrusive
            setTimeout(() => setShow(true), 4000);
        };
        window.addEventListener("beforeinstallprompt", onPrompt);

        // iOS — no event ever fires, just check
        if (isIOS() && !isStandalone()) {
            setTimeout(() => {
                setIosFallback(true);
                setShow(true);
            }, 6000);
        }

        // After actual install
        const onInstalled = () => {
            setShow(false);
            setDeferred(null);
        };
        window.addEventListener("appinstalled", onInstalled);

        return () => {
            window.removeEventListener("beforeinstallprompt", onPrompt);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const dismiss = () => {
        localStorage.setItem(DISMISSED_KEY, String(Date.now()));
        setShow(false);
    };

    const install = async () => {
        if (!deferred) return;
        deferred.prompt();
        try {
            await deferred.userChoice;
        } catch {/* user dismissed */}
        setDeferred(null);
        setShow(false);
    };

    if (!show) return null;

    return (
        <div
            className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:w-[380px] z-[60] animate-[fadeUp_400ms_ease]"
            data-testid="pwa-install-prompt"
        >
            <div className="bg-card border border-primary/40 rounded-sm shadow-[0_20px_60px_-15px_rgba(0,0,0,0.6)] overflow-hidden">
                <div className="px-5 py-4 flex items-center justify-between border-b border-white/5">
                    <div className="flex items-center gap-2">
                        <Smartphone size={14} className="gold-text" strokeWidth={1.5} />
                        <div className="overline">Install SMART</div>
                    </div>
                    <button
                        onClick={dismiss}
                        className="text-muted-foreground hover:text-foreground"
                        aria-label="Close"
                        data-testid="pwa-prompt-close"
                    >
                        <X size={16} strokeWidth={1.5} />
                    </button>
                </div>
                <div className="p-5">
                    {iosFallback ? (
                        <>
                            <p className="text-sm text-foreground leading-relaxed mb-4">
                                Add SMART to your home screen for one-tap booking, 24/7.
                            </p>
                            <ol className="text-xs text-muted-foreground space-y-2 mb-4 leading-relaxed">
                                <li className="flex gap-2">
                                    <span className="gold-text">1.</span>
                                    Tap the <Share size={11} className="inline -mt-0.5" /> Share icon at the bottom of Safari
                                </li>
                                <li className="flex gap-2">
                                    <span className="gold-text">2.</span>
                                    Scroll and tap <strong className="text-foreground">"Add to Home Screen"</strong>
                                </li>
                                <li className="flex gap-2">
                                    <span className="gold-text">3.</span>
                                    Tap <strong className="text-foreground">Add</strong> — done.
                                </li>
                            </ol>
                            <button
                                onClick={dismiss}
                                className="w-full bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-10 hover:bg-primary/90 transition-colors"
                                data-testid="pwa-prompt-got-it"
                            >
                                Got it
                            </button>
                        </>
                    ) : (
                        <>
                            <p className="text-sm text-foreground leading-relaxed mb-2">
                                Faster than the browser. Lives on your home screen.
                            </p>
                            <p className="text-xs text-muted-foreground mb-5 leading-relaxed">
                                One-tap booking, offline catalog, push-ready for safety pings.
                            </p>
                            <div className="flex gap-2">
                                <button
                                    onClick={install}
                                    className="flex-1 bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-10 hover:bg-primary/90 transition-colors"
                                    data-testid="pwa-prompt-install"
                                >
                                    Install
                                </button>
                                <button
                                    onClick={dismiss}
                                    className="px-4 border border-white/10 rounded-sm uppercase tracking-[0.2em] text-xs h-10 text-muted-foreground hover:text-foreground hover:border-white/30 transition-colors"
                                    data-testid="pwa-prompt-later"
                                >
                                    Later
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
